import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Contact, Plus, Search } from "lucide-react";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const EMPTY = { name: "", email: "", phone: "", organization: "", job_title: "", address: "", tags: [], notes: "" };

export const ContactEditor = ({ editor, setEditor, onSaved }) => {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") setEditor(null); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [setEditor]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...editor, tags: typeof editor.tags === "string" ? editor.tags.split(",") : editor.tags || [] };
      if (editor.id) await api.put(`/helpdesk/crm/contacts/${editor.id}`, payload);
      else await api.post("/helpdesk/crm/contacts", payload);
      toast.success("Contact saved");
      onSaved();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-neutral-900/40 z-[60] flex items-center justify-center px-4" data-testid="crm-editor-modal">
      <form onSubmit={save} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-md w-full p-7 max-h-[88vh] overflow-y-auto">
        <h3 className="font-semibold font-heading text-lg mb-4">{editor.id ? "Edit contact" : "New contact"}</h3>
        <label className="text-sm font-medium text-neutral-700">Name
          <input data-testid="crm-name-input" required value={editor.name} onChange={(e) => setEditor((f) => ({ ...f, name: e.target.value }))} className={inputCls} /></label>
        <label className="text-sm font-medium text-neutral-700 block mt-3">Email
          <input data-testid="crm-email-input" type="email" required value={editor.email} onChange={(e) => setEditor((f) => ({ ...f, email: e.target.value }))} className={inputCls} /></label>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <label className="text-xs text-neutral-500">Phone
            <input data-testid="crm-phone-input" value={editor.phone || ""} onChange={(e) => setEditor((f) => ({ ...f, phone: e.target.value }))} className={inputCls} /></label>
          <label className="text-xs text-neutral-500">Organization
            <input data-testid="crm-org-input" value={editor.organization || ""} onChange={(e) => setEditor((f) => ({ ...f, organization: e.target.value }))} className={inputCls} /></label>
          <label className="text-xs text-neutral-500">Job title
            <input data-testid="crm-jobtitle-input" value={editor.job_title || ""} onChange={(e) => setEditor((f) => ({ ...f, job_title: e.target.value }))} className={inputCls} /></label>
          <label className="text-xs text-neutral-500">Tags (comma separated)
            <input data-testid="crm-tags-input" value={Array.isArray(editor.tags) ? editor.tags.join(", ") : editor.tags || ""}
              onChange={(e) => setEditor((f) => ({ ...f, tags: e.target.value }))} className={inputCls} placeholder="vip, author" /></label>
        </div>
        <label className="text-xs text-neutral-500 block mt-3">Address
          <input data-testid="crm-address-input" value={editor.address || ""} onChange={(e) => setEditor((f) => ({ ...f, address: e.target.value }))} className={inputCls} /></label>
        <label className="text-xs text-neutral-500 block mt-3">Notes
          <textarea data-testid="crm-notes-input" rows={3} value={editor.notes || ""} onChange={(e) => setEditor((f) => ({ ...f, notes: e.target.value }))} className={inputCls} /></label>
        <div className="flex gap-2 mt-5">
          <button data-testid="crm-save-button" disabled={busy} className="flex-1 py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">{busy ? "Saving..." : "Save contact"}</button>
          <button type="button" onClick={() => setEditor(null)} className="px-5 py-2.5 rounded-md border border-neutral-300 text-sm font-medium hover:bg-neutral-50">Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default function Crm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [contacts, setContacts] = useState([]);
  const [editor, setEditor] = useState(null);

  const load = useCallback(() => {
    api.get(`/helpdesk/crm/contacts?q=${encodeURIComponent(q)}`).then((r) => setContacts(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="desk-crm-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2"><Contact size={22} /> CRM</h1>
        <button data-testid="crm-new-contact" onClick={() => setEditor({ ...EMPTY })}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">
          <Plus size={15} /> New contact
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-5">Every requester is added automatically when a ticket is created — from the desk or the public support page.</p>
      <div className="relative mb-4 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="crm-search" placeholder="Search name, email, organization..." value={q} onChange={(e) => setQ(e.target.value)}
          className="w-full bg-white border border-neutral-300 rounded-md pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none" />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {contacts.length === 0 ? <p className="p-6 text-sm text-slate-400" data-testid="crm-empty">No contacts yet — they appear automatically when tickets come in.</p> : (
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm text-left">
            <thead><tr className="bg-slate-50">
              {["Name", "Email", "Organization", "Phone", "Tags", "Tickets", "Last activity"].map((h) => (
                <th key={h} className="px-5 py-3 font-medium text-slate-500 uppercase tracking-wider text-xs">{h}</th>))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {contacts.map((c) => (
                <tr key={c.id} data-testid={`crm-row-${c.id}`} onClick={() => navigate(`/desk/crm/${c.id}`)} className="hover:bg-slate-50 cursor-pointer">
                  <td className="px-5 py-3 font-medium">{c.name}</td>
                  <td className="px-5 py-3 text-slate-500">{c.email}</td>
                  <td className="px-5 py-3 text-slate-500">{c.organization || "—"}</td>
                  <td className="px-5 py-3 text-slate-500">{c.phone || "—"}</td>
                  <td className="px-5 py-3">{c.tags?.length ? c.tags.slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] font-semibold bg-teal-50 text-teal-800 border border-teal-200 rounded-full px-2 py-0.5 mr-1">{t}</span>)) : <span className="text-slate-300">—</span>}</td>
                  <td className="px-5 py-3"><span className="text-xs font-semibold bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">{c.ticket_count}</span></td>
                  <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{new Date(c.last_activity_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
      {editor && <ContactEditor editor={editor} setEditor={setEditor} onSaved={() => { setEditor(null); load(); }} />}
    </div>
  );
}
